import { useState } from 'react'
import { Film, Zap, Loader, Download, RefreshCw, Upload } from 'lucide-react'
import useStore from '../store/useStore'
import { buildLTXWorkflow, estimateCost, submitJob, pollJob } from '../api/runpod'

const DURATIONS = [4, 6, 8, 10]
const MOTION_STYLES = ['Slow + cinematic', 'Natural movement', 'Dynamic + fast', 'Static camera']

export default function QuickVideoGenerator() {
  const { characters, addJob, updateJob, completeJob, failJob, addClip, addNotification } = useStore()

  const [prompt, setPrompt] = useState('')
  const [selectedCharacter, setSelectedCharacter] = useState('')
  const [duration, setDuration] = useState(8)
  const [motionStyle, setMotionStyle] = useState('Natural movement')
  const [startFrameUrl, setStartFrameUrl] = useState('')
  const [endFrameUrl, setEndFrameUrl] = useState('')
  const [generating, setGenerating] = useState(false)
  const [result, setResult] = useState(null)

  const character = characters.find(c => c.id === selectedCharacter)
  const cost = estimateCost('ltx_video')

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      addNotification({ type: 'error', message: 'Enter a motion description first.' })
      return
    }

    const motionMap = {
      'Slow + cinematic': 'slow camera movement, cinematic, dramatic',
      'Natural movement': 'natural fluid movement, realistic motion',
      'Dynamic + fast': 'dynamic fast movement, energetic, quick cuts',
      'Static camera': 'static camera, no camera movement, locked shot',
    }

    const fullPrompt = [
      character?.trigger_word,
      prompt.trim(),
      motionMap[motionStyle],
    ].filter(Boolean).join(', ')

    const jobId = `vid_${Date.now()}`
    setGenerating(true)
    setResult(null)
    addJob({ id: jobId, label: `Video ${duration}s — LTX-2`, type: 'ltx_video', status: 'IN_QUEUE' })

    try {
      const workflow = buildLTXWorkflow({
        prompt: fullPrompt,
        startFrameUrl: startFrameUrl || null,
        endFrameUrl: endFrameUrl || null,
        duration,
        loraName: character?.lora_path || null,
      })

      const { id: runpodJobId } = await submitJob(workflow)
      const output = await pollJob(runpodJobId, (status) => {
        updateJob(jobId, { status })
      })

      const videoUrl = output?.video_url || output?.video || null
      if (videoUrl) {
        const clip = {
          id: `clip_${Date.now()}`,
          url: videoUrl,
          prompt: fullPrompt,
          model: 'LTX-2',
          duration,
          character: character?.name || 'None',
          createdAt: Date.now(),
        }
        setResult(clip)
        addClip(clip)
      }

      completeJob(jobId, { output })
      addNotification({ type: 'success', message: `Video generated — ${duration} seconds.` })
    } catch (err) {
      failJob(jobId, err.message)
      addNotification({ type: 'error', message: `Generation failed: ${err.message}` })
    } finally {
      setGenerating(false)
    }
  }

  const handleDownload = () => {
    if (!result?.url) return
    const a = document.createElement('a')
    a.href = result.url
    a.download = `aiq-video-${result.id}.mp4`
    a.click()
  }

  return (
    <div className="flex-1 overflow-y-auto p-6">
      <div className="mb-6">
        <h2 className="text-xl font-bold text-white flex items-center gap-2">
          <Film size={22} className="text-purple-400" />
          Quick Video Generator
        </h2>
        <p className="text-slate-400 text-sm mt-1">Generate a video clip fast. No canvas needed.</p>
      </div>

      <div className="grid grid-cols-3 gap-6">

        {/* Left — Controls */}
        <div className="col-span-1 space-y-4">

          {/* Character */}
          {characters.length > 0 && (
            <div className="card p-4">
              <label className="label">Character LoRA</label>
              <select
                value={selectedCharacter}
                onChange={e => setSelectedCharacter(e.target.value)}
                className="input text-sm"
              >
                <option value="">No character</option>
                {characters.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
              {character && (
                <p className="text-xs text-brand-400 mt-1 font-mono">{character.trigger_word}</p>
              )}
            </div>
          )}

          {/* Duration */}
          <div className="card p-4">
            <label className="label">Duration</label>
            <div className="flex gap-2">
              {DURATIONS.map(d => (
                <button
                  key={d}
                  onClick={() => setDuration(d)}
                  className={`flex-1 py-2 text-sm rounded-lg border transition-colors font-medium ${
                    duration === d
                      ? 'bg-brand-500 border-brand-400 text-white'
                      : 'bg-surface border-surface-border text-slate-400 hover:border-brand-500/50'
                  }`}
                >
                  {d}s
                </button>
              ))}
            </div>
            {cost && (
              <p className="text-xs text-green-400 mt-2">Est. cost: {cost.estimatedCost}</p>
            )}
          </div>

          {/* Motion style */}
          <div className="card p-4">
            <label className="label">Camera / Motion</label>
            <div className="space-y-1.5">
              {MOTION_STYLES.map(s => (
                <button
                  key={s}
                  onClick={() => setMotionStyle(s)}
                  className={`w-full text-left px-3 py-2 text-sm rounded-lg border transition-colors ${
                    motionStyle === s
                      ? 'bg-brand-500/15 border-brand-500/30 text-brand-300'
                      : 'bg-surface border-surface-border text-slate-400 hover:border-brand-500/30'
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>

          {/* Motion description */}
          <div className="card p-4 space-y-3">
            <div>
              <label className="label">Motion Description</label>
              <textarea
                rows={4}
                className="input text-sm resize-none"
                placeholder="Character walks toward camera, gestures with hands explaining, warm studio lighting..."
                value={prompt}
                onChange={e => setPrompt(e.target.value)}
              />
            </div>

            {/* Frame conditioning */}
            <div>
              <label className="label">Start Frame URL (optional)</label>
              <input
                type="text"
                className="input text-sm"
                placeholder="URL of generated start frame..."
                value={startFrameUrl}
                onChange={e => setStartFrameUrl(e.target.value)}
              />
              <p className="text-xs text-slate-500 mt-1">Pin the first frame — locks character consistency</p>
            </div>
            <div>
              <label className="label">End Frame URL (optional)</label>
              <input
                type="text"
                className="input text-sm"
                placeholder="URL of generated end frame..."
                value={endFrameUrl}
                onChange={e => setEndFrameUrl(e.target.value)}
              />
            </div>
          </div>

          {/* Generate */}
          <button
            onClick={handleGenerate}
            disabled={generating || !prompt.trim()}
            className="w-full justify-center py-3 text-base font-semibold rounded-xl flex items-center gap-2
              text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ background: 'linear-gradient(135deg, #8b5cf6, #6272f1)' }}
          >
            {generating ? (
              <><Loader size={18} className="generation-spinner" /> Generating {duration}s clip...</>
            ) : (
              <><Zap size={18} /> Generate Video</>
            )}
          </button>

        </div>

        {/* Right — Result */}
        <div className="col-span-2">
          {!result ? (
            <div className="card h-full flex flex-col items-center justify-center p-12 text-center">
              <Film size={56} className="text-slate-700 mb-4" />
              <h3 className="text-white font-medium mb-2">Your video appears here</h3>
              <p className="text-slate-500 text-sm max-w-xs">
                Configure settings, add a motion description, and hit Generate. Takes about 90 seconds on A40.
              </p>
              {generating && (
                <div className="mt-8 text-center">
                  <Loader size={28} className="text-purple-400 generation-spinner mx-auto mb-3" />
                  <p className="text-purple-300 text-sm font-medium">Generating on your A40 GPU...</p>
                  <p className="text-slate-500 text-xs mt-1">~60-90 seconds</p>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {/* Video player */}
              <div className="card overflow-hidden">
                <video
                  src={result.url}
                  controls
                  autoPlay
                  loop
                  className="w-full"
                />
              </div>

              {/* Metadata */}
              <div className="card p-4">
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <div className="text-slate-500 text-xs mb-1">Model</div>
                    <div className="text-slate-200 font-medium">{result.model}</div>
                  </div>
                  <div>
                    <div className="text-slate-500 text-xs mb-1">Duration</div>
                    <div className="text-slate-200 font-medium">{result.duration}s</div>
                  </div>
                  <div>
                    <div className="text-slate-500 text-xs mb-1">Character</div>
                    <div className="text-slate-200 font-medium">{result.character}</div>
                  </div>
                </div>
                <div className="mt-3 pt-3 border-t border-surface-border">
                  <div className="text-slate-500 text-xs mb-1">Prompt</div>
                  <div className="text-slate-400 text-xs leading-relaxed">{result.prompt}</div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3">
                <button onClick={handleDownload} className="btn-primary flex-1 justify-center">
                  <Download size={16} /> Download MP4
                </button>
                <button onClick={handleGenerate} disabled={generating} className="btn-secondary flex-1 justify-center">
                  <RefreshCw size={16} /> Generate Again
                </button>
              </div>

              {/* Clip chaining tip */}
              <div className="card p-3 border border-brand-500/20 bg-brand-500/5">
                <p className="text-xs text-brand-300">
                  💡 <strong>Chain clips:</strong> Use the last frame of this video as the Start Frame URL for your next clip to create seamless continuity.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
